/**
 * Announcements ticker editor.
 *
 * Each announcement is short (label + message + optional link + window).
 * Instead of a separate detail screen, the list opens a side panel /
 * inline editor for fast in-place editing.
 *
 * Public site reads `/public/announcements` and renders the live ticker.
 */
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Plus, Trash2, MoreHorizontal, GripVertical } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";

import { api } from "@/lib/api";
import { fetchAll } from "@/lib/fetchAll";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { StatusBadge } from "@/components/StatusBadge";
import { PageHeader } from "@/components/PageHeader";

export const Route = createFileRoute("/_app/content/announcements/")({ component: AnnouncementsPage });

interface AnnouncementRow {
  id: number;
  label: string;
  message: string;
  link_url: string | null;
  priority: number;
  starts_at: number | null;
  ends_at: number | null;
  status: "draft" | "live" | "archived";
  updated_at: number;
}

const schema = z.object({
  label: z.string().min(1).max(60),
  message: z.string().min(1).max(500),
  link_url: z.string().url().or(z.literal("")).nullable(),
  priority: z.coerce.number().int().min(0).max(100),
  status: z.enum(["draft", "live", "archived"]),
  starts_at_iso: z.string().optional(),
  ends_at_iso: z.string().optional(),
});
type FormValues = z.infer<typeof schema>;

function isoToUnix(iso?: string): number | null { return iso ? Math.floor(new Date(iso).getTime() / 1000) : null; }
function unixToIso(unix: number | null): string { return unix ? new Date(unix * 1000).toISOString().slice(0, 16) : ""; }

function AnnouncementsPage() {
  const { can } = useAuth();
  const qc = useQueryClient();
  const [editing, setEditing] = useState<AnnouncementRow | null>(null);
  const [creating, setCreating] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["announcements", "list"],
    queryFn: () => fetchAll<AnnouncementRow>("/admin/announcements"),
  });

  const remove = useMutation({
    mutationFn: (id: number) => api.del(`/admin/announcements/${id}`),
    onSuccess: () => { toast.success("Removed"); qc.invalidateQueries({ queryKey: ["announcements"] }); },
  });

  return (
    <div className="space-y-2">
      <PageHeader
        title="Announcements"
        description="Short ticker items shown across the public site. Higher priority surfaces first."
        actions={can("announcements", "create") && (
          <Button onClick={() => setCreating(true)}><Plus className="h-4 w-4" /> New announcement</Button>
        )}
      />

      <Card>
        <CardContent className="px-0 pt-0 pb-0">
          {isLoading ? (
            <p className="py-12 text-center text-sm text-muted-foreground">Loading…</p>
          ) : (data?.items ?? []).length === 0 ? (
            <p className="py-12 text-center text-sm text-muted-foreground">Nothing scheduled.</p>
          ) : (
            <ul className="divide-y">
              {data!.items.map((a) => (
                <li key={a.id} className="flex items-center gap-3 px-4 py-3">
                  <GripVertical className="h-4 w-4 text-muted-foreground" />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] uppercase tracking-wider font-semibold text-primary bg-primary/10 px-2 py-0.5 rounded">{a.label}</span>
                      <StatusBadge status={a.status} />
                      <span className="text-xs text-muted-foreground tabular-nums">prio {a.priority}</span>
                    </div>
                    <p className="mt-1 truncate text-sm text-foreground">{a.message}</p>
                    {(a.starts_at || a.ends_at) && (
                      <p className="mt-1 text-xs text-muted-foreground">
                        {a.starts_at && `from ${format(a.starts_at * 1000, "MMM d, h:mma")}`}
                        {a.starts_at && a.ends_at && " · "}
                        {a.ends_at && `until ${format(a.ends_at * 1000, "MMM d, h:mma")}`}
                      </p>
                    )}
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8"><MoreHorizontal className="h-4 w-4" /></Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => setEditing(a)}>Edit</DropdownMenuItem>
                      <DropdownMenuItem
                        disabled={!can("announcements", "delete")}
                        className="text-destructive focus:text-destructive"
                        onClick={() => { if (confirm("Remove this announcement?")) remove.mutate(a.id); }}
                      ><Trash2 className="h-3.5 w-3.5" /> Delete</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <AnnouncementDialog
        open={creating || !!editing}
        onOpenChange={(b) => { if (!b) { setCreating(false); setEditing(null); } }}
        announcement={editing}
      />
    </div>
  );
}

function AnnouncementDialog({
  open, onOpenChange, announcement,
}: {
  open: boolean;
  onOpenChange: (b: boolean) => void;
  announcement: AnnouncementRow | null;
}) {
  const qc = useQueryClient();
  const isEdit = !!announcement;

  const { register, handleSubmit, reset, watch, setValue, formState: { errors } } = useForm<FormValues>({
    resolver: zodResolver(schema),
    values: {
      label: announcement?.label ?? "",
      message: announcement?.message ?? "",
      link_url: announcement?.link_url ?? "",
      priority: announcement?.priority ?? 0,
      status: (announcement?.status ?? "live") as "draft" | "live" | "archived",
      starts_at_iso: unixToIso(announcement?.starts_at ?? null),
      ends_at_iso: unixToIso(announcement?.ends_at ?? null),
    },
  });

  const save = useMutation({
    mutationFn: async (v: FormValues) => {
      const body = {
        label: v.label,
        message: v.message,
        link_url: v.link_url || null,
        priority: v.priority,
        status: v.status,
        starts_at: isoToUnix(v.starts_at_iso),
        ends_at: isoToUnix(v.ends_at_iso),
      };
      if (isEdit && announcement) return api.patch(`/admin/announcements/${announcement.id}`, body);
      return api.post("/admin/announcements", body);
    },
    onSuccess: () => {
      toast.success(isEdit ? "Saved" : "Created");
      qc.invalidateQueries({ queryKey: ["announcements"] });
      reset();
      onOpenChange(false);
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Save failed"),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit announcement" : "New announcement"}</DialogTitle>
          <DialogDescription>Shows up in the homepage ticker and across all public pages.</DialogDescription>
        </DialogHeader>
        <form className="space-y-4" onSubmit={handleSubmit((v) => save.mutate(v))}>
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1.5 col-span-1">
              <Label htmlFor="label">Label</Label>
              <Input id="label" placeholder="New" maxLength={60} {...register("label")} />
              {errors.label && <p className="text-xs text-destructive">{errors.label.message}</p>}
            </div>
            <div className="space-y-1.5 col-span-2">
              <Label htmlFor="link_url">Link URL (optional)</Label>
              <Input id="link_url" placeholder="https://krct.ac.in/admissions" {...register("link_url")} />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="message">Message</Label>
            <Input id="message" placeholder="Admissions 2026–27 are now open across all 9 departments" maxLength={500} {...register("message")} />
            {errors.message && <p className="text-xs text-destructive">{errors.message.message}</p>}
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="priority">Priority</Label>
              <Input id="priority" type="number" min={0} max={100} {...register("priority")} />
              <p className="text-xs text-muted-foreground">Higher → earlier in ticker</p>
            </div>
            <div className="space-y-1.5">
              <Label>Status</Label>
              <Select value={watch("status")} onValueChange={(v) => setValue("status", v as "draft" | "live" | "archived")}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="live">Live</SelectItem>
                  <SelectItem value="archived">Archived</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="starts_at_iso">Starts at</Label>
              <Input id="starts_at_iso" type="datetime-local" {...register("starts_at_iso")} />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ends_at_iso">Ends at</Label>
            <Input id="ends_at_iso" type="datetime-local" {...register("ends_at_iso")} />
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={save.isPending}>{save.isPending ? "Saving…" : "Save"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
